import {
  useDrawingContext,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { ReactNode } from 'react';

import { AISMapProvider } from './AISMapContext';
import { AISMapLayer } from './AISMapLayer';
import { ListenForShipClicks } from './ListenForShipClicks';
import { MapPanel } from './MapPanel/MapPanel';
import { useDrawings } from './useDrawings';
import { useSettings } from '../Settings/SettingsProvider';
import { useZoomAndSelectShip } from './useZoomAndSelectShip';
import { PortLayer } from './PortLayer';
import { ListenForLayerClicks } from './ListenForLayerClicks';
import {
  useSelectedGpsLostGridCellStyling,
  useSelectedPortStyling,
  useSelectedSourceIdentifiersStyling,
} from './useSelectedStyling';
import { CoverageLayer } from './Coverage/CoverageLayer';
import { useCursorCoordinates } from './useCursorCoordinates';
import { useCheckSearchAndRescue } from '../../../../../libs/websockets/src/lib/ol-server/useCheckSearchAndRescue';
import { TargetsOnMapProvider } from './TargetsOnMapContext';
import { HoverMap } from './HoverMap';
import { AtonProvider } from '../Atons/AtonContext';
import { RoutesProvider } from '../Routes/RoutesContext';
import { useZoomToExternalShip } from './useZoomToExternalShip';
import { usePlayReplay } from '../Replay/usePlayReplay';
import { GPSLostLayer } from './GpsLost/GPSLostLayer';
import { useOnStartLayers } from './onMapStartup/LayersOnStart/useOnStartLayers';
import { useOnMapStart } from './onMapStartup/useOnMapStart';
import { usePopupEventHandler } from '../Popup';
import { usePopupDataProviders } from '../Popup/usePopupDataProviders';
import { PortTimelineDataProvider } from '../Popup/PortTimelineDataProvider';
import { ArrivalsDataProvider } from '../Popup/ArrivalsDataProvider';
import { ReportShipDataProvider } from '../Popup/ReportShipDataProvider';
import { ReportDataProvider } from '../Popup/ReportDataProvider';
import { EventsDataProvider } from '../Popup/EventsDataProvider';
import { VesselTimelineDataProvider } from '../Popup/VesselTimelineDataProvider';
import { TargetsDataProvider } from '../Popup/TargetsDataProvider';
import { SettingsDataProvider } from '../Popup/SettingsDataProvider';
import { EventsProvider } from '../Events/EventsContext';
import {
  MapContextMenuProvider,
  MapContextMenu,
  ListenForContextMenu,
} from './ContextMenu';

export interface MapComponentsProps {
  children?: ReactNode;
}

export interface AISComponentsProps {
  children?: ReactNode;
}

function AISComponents({ children }: AISComponentsProps) {
  const drawingContext = useDrawingContext();
  const { map } = useOpenLayersContext();
  useZoomAndSelectShip();
  useCursorCoordinates();
  useZoomToExternalShip();
  usePlayReplay();
  usePopupEventHandler();
  usePopupDataProviders();

  const { settings } = useSettings();
  useCheckSearchAndRescue(
    Boolean(settings['ribbonPlugin.settings']?.emergency_alerts_mdm)
  );
  useSelectedPortStyling();
  useSelectedSourceIdentifiersStyling();
  useSelectedGpsLostGridCellStyling();

  return (
    <>
      <AISMapLayer />
      <PortLayer />
      <CoverageLayer />
      <GPSLostLayer />
      {/* Popup data providers - handle requests from popup windows */}
      <PortTimelineDataProvider />
      <ArrivalsDataProvider />
      <ReportShipDataProvider />
      <ReportDataProvider />
      <VesselTimelineDataProvider />
      <TargetsDataProvider />
      <SettingsDataProvider />
      {/* EventsProvider wraps components that need shared events data */}
      <EventsProvider>
        {/* EventsDataProvider responds to popup init requests using EventsContext data */}
        <EventsDataProvider />
        {!drawingContext.geometryType && (
          <>
            <ListenForShipClicks></ListenForShipClicks>
            <ListenForLayerClicks map={map}></ListenForLayerClicks>
            <ListenForContextMenu map={map}></ListenForContextMenu>
            <HoverMap />
          </>
        )}
        <MapPanel></MapPanel>
      </EventsProvider>
      {children}
    </>
  );
}

export function MapComponents({ children }: MapComponentsProps) {
  // This hook takes care of everything that's drawn in MDM
  useDrawings();
  useOnMapStart();

  return (
    <AISMapProvider>
      <AtonProvider>
        <TargetsOnMapProvider>
          <RoutesProvider>
            <MapContextMenuProvider>
              <AISComponents>{children}</AISComponents>
              <MapContextMenu />
            </MapContextMenuProvider>
          </RoutesProvider>
        </TargetsOnMapProvider>
      </AtonProvider>
    </AISMapProvider>
  );
}
